import { useFormik } from 'formik';
import {
  ProjectPageGetProjectByIdDocument,
  useUnarchiveAssessmentStageDeiActionMutation,
  useUpsertAssessmentStageDeiActionMutation,
} from 'generated/graphql';
import * as yup from 'yup';
import {
  Button,
  CheckboxGroup,
  CheckboxGroupItem,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { deiCheckboxOptions, DeiFieldValue, getDeiFields } from './dei.helpers';
import styles from './DeiActionForm.module.scss';

const validationSchema = yup.object().shape({
  deiValues: yup
    .array(yup.string().required())
    .min(1, 'Please select at least one question.'),
});

const DeiActionForm = ({
  stageId,
  projectId,
  onClose,
  type,
  initialValues,
  completedCount,
  hasArchivedDeiForm = false,
}: {
  stageId: string;
  projectId: number;
  onClose: () => void;
  type: 'create' | 'edit';
  initialValues: {
    deiValues: DeiFieldValue[];
  };
  completedCount?: number;
  hasArchivedDeiForm?: boolean;
}) => {
  const { handleMsgType } = useNotification();
  const buttonText = type === 'create' ? 'Add action' : 'Save';
  const notificationVerb = type === 'create' ? 'created' : 'updated';

  const handleOnCompleted = () => {
    onClose();
    handleMsgType({
      type: TNotification.success,
      title: `Demographic information action ${notificationVerb}`,
    });
  };

  const handleOnError = () => {
    handleMsgType({
      type: TNotification.error,
      title: `Demographic information action could not be ${notificationVerb}.`,
      message: 'Something went wrong. Please try again.',
    });
  };

  const [upsertDeiForm, { loading }] =
    useUpsertAssessmentStageDeiActionMutation({
      onCompleted: handleOnCompleted,
      onError: handleOnError,
      refetchQueries: [
        {
          query: ProjectPageGetProjectByIdDocument,
          variables: { id: projectId },
        },
      ],
    });

  const [unarchiveDeiForm, { loading: loadingUnarchive }] =
    useUnarchiveAssessmentStageDeiActionMutation({
      onCompleted: handleOnCompleted,
      onError: handleOnError,
      refetchQueries: [
        {
          query: ProjectPageGetProjectByIdDocument,
          variables: { id: projectId },
        },
      ],
    });

  const handleDeiSubmit = () => {
    if (Boolean(errors.deiValues)) return;

    const payload = {
      variables: {
        stageId,
        fields: getDeiFields(values.deiValues),
      },
    };

    if (hasArchivedDeiForm) {
      unarchiveDeiForm(payload);
      return;
    }
    upsertDeiForm(payload);
  };

  const { setFieldValue, values, errors, dirty } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnChange: true,
    onSubmit: handleDeiSubmit,
  });

  const showPreviouslyArchivedNotification = hasArchivedDeiForm;
  const showCompletedCountNotification =
    type === 'edit' && Boolean(completedCount) && dirty;

  const handleChange = (value: string[]) => {
    setFieldValue('deiValues', value as DeiFieldValue[]);
  };

  return (
    <div className={styles.container} data-testid="edit-stage__dei-action-form">
      <div>
        <p className={styles.description}>
          This action checks for adverse impact for this specific client and
          contributes to broader adverse impact studies for our product.
        </p>
      </div>
      {showPreviouslyArchivedNotification && (
        <InlineNotification
          notificationType={NotificationType.NEUTRAL}
          className={styles.archivedInlineNotification}
        >
          This role had a demographic information action in the past.
        </InlineNotification>
      )}
      <div className={styles.formContainer}>
        <p className={styles.formTitle}> Questions to include</p>
        <CheckboxGroup
          id="dei-checkbox-group"
          value={values.deiValues}
          onChange={handleChange}
          hasError={Boolean(errors.deiValues)}
          error={`${errors.deiValues ? errors.deiValues : ''}`}
        >
          {deiCheckboxOptions.map(({ label, value }, index) => {
            return (
              <CheckboxGroupItem
                key={index}
                data-testid={`dei-form__dei-option-${value}`}
                id={value}
                label={label}
                name={value}
              />
            );
          })}
        </CheckboxGroup>
      </div>
      {showCompletedCountNotification && (
        <div className={styles.completedInlineNotificationWrapper}>
          <InlineNotification
            notificationType={NotificationType.NEUTRAL}
            className={styles.completedInlineNotification}
          >
            Adding or removing questions will create inconsistencies in data
            collected between past and future candidates for internal users and
            or clients.
            <p className={styles.description}>
              Number of completed demographic information questionnaires{' '}
              <span className={styles.completedForms}>{completedCount}</span>
            </p>
          </InlineNotification>
        </div>
      )}
      <div className={styles.actionsContainer}>
        <Button
          size="medium"
          type="submit"
          loading={loading || loadingUnarchive}
          onClick={handleDeiSubmit}
          data-testid="edit-stage__dei-action-form-save-button"
        >
          {buttonText}
        </Button>
        <Button onClick={onClose} size="medium" variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default DeiActionForm;
